import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, UserPlus, BookOpen, Heart, Clock, ShieldCheck, Star, Feather } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const steps = [
  {
    title: 'Create Child Profile',
    description: 'Tell us about your young hero in 2 minutes.',
    accent: 'from-purple-500 to-pink-500',
    icon: UserPlus,
  },
  {
    title: 'Pick Adventure',
    description: 'Choose from science, fairy tales, sports, and more.',
    accent: 'from-yellow-500 to-orange-500',
    icon: BookOpen,
  },
];

const badges = [
  { label: '2 min setup', icon: Clock },
  { label: 'Parent-approved', icon: ShieldCheck },
  { label: 'Loved by kids', icon: Heart },
];

const StoriesEmptyState = () => {
  const navigate = useNavigate();

  const handleCreateProfile = () => navigate('/profiles/create');
  const handleExploreStories = () => navigate('/stories');

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 flex flex-col">
      <Header />
      <main className="container mx-auto px-4 py-12 flex-1">
        <div className="relative mb-12">
          <div className="absolute inset-0 -z-10 blur-3xl opacity-50">
            <div className="w-40 h-40 bg-purple-200 rounded-full absolute -top-10 left-0" />
            <div className="w-32 h-32 bg-pink-200 rounded-full absolute top-10 right-10" />
            <div className="w-24 h-24 bg-yellow-200 rounded-full absolute bottom-0 left-1/3" />
          </div>

          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center px-5 py-2 bg-white/70 rounded-full border border-purple-100 text-purple-700 font-semibold text-sm mb-6 shadow-sm">
              <Sparkles className="h-4 w-4 mr-2" />
              Your child’s story adventure starts now
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              Create your child’s <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">first story</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Turn bedtime into the best time. Create a profile, add their favorite people, and generate magical adventures in seconds.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                type="button"
                onClick={handleCreateProfile}
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
              >
                Create Profile Now
              </button>
              <button
                type="button"
                onClick={handleExploreStories}
                className="px-8 py-3 border border-purple-200 text-purple-700 bg-white rounded-full font-semibold hover:border-purple-400 transition-colors"
              >
                Browse Stories
              </button>
            </div>
          </div>
        </div>

        <section className="grid md:grid-cols-2 gap-8 mb-12">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={step.title} className="bg-white rounded-3xl p-8 shadow-2xl border border-purple-100">
                <div className="flex flex-col items-center text-center">
                  <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.accent} flex items-center justify-center text-white text-3xl mb-4`}>
                    {index + 1}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h2>
                  <p className="text-gray-600 mb-6">{step.description}</p>
                  <Icon className="h-10 w-10 text-gray-300" />
                </div>
              </div>
            );
          })}
        </section>

        <section className="bg-white rounded-3xl border border-yellow-100 shadow-2xl p-8 mb-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="rounded-2xl border-4 border-dashed border-yellow-300 p-6 bg-gradient-to-br from-yellow-50 to-orange-50 shadow-inner">
                <p className="text-xl font-semibold text-gray-800 mb-4">[Your Child’s Name] and the Skyline Circus</p>
                <p className="text-gray-700 leading-relaxed">
                  Tonight, [Name] teams up with [Best Friend] to rescue the flying acrobats before the show begins. They’ll need courage, laughter, and a sprinkle of cosmic confetti!
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Imagine their reaction</h3>
              <p className="text-gray-600 mb-6">
                Every story is unique to your child’s world. Start by creating their profile, then bring their adventures to life any time.
              </p>
              <div className="flex flex-wrap gap-3 mb-6">
                {badges.map((badge) => {
                  const Icon = badge.icon;
                  return (
                    <span key={badge.label} className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-full text-sm font-medium">
                      <Icon className="h-4 w-4" />
                      {badge.label}
                    </span>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={handleCreateProfile}
                className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl font-semibold shadow-lg transition-colors"
              >
                Start with a profile
              </button>
            </div>
          </div>
        </section>

        <section className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl border border-purple-100 p-6 shadow-xl">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mb-4">
              <Star className="h-6 w-6 text-purple-600" />
            </div>
            <h4 className="text-lg font-bold mb-2">Motivate daily reading</h4>
            <p className="text-gray-600">Reward streaks, celebrate milestones, and keep the bedtime hype alive.</p>
          </div>
          <div className="bg-white rounded-2xl border border-pink-100 p-6 shadow-xl">
            <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mb-4">
              <Feather className="h-6 w-6 text-pink-500" />
            </div>
            <h4 className="text-lg font-bold mb-2">Personalized prompts</h4>
            <p className="text-gray-600">Add friends, teachers, even pets. Each detail makes the story unforgettable.</p>
          </div>
          <div className="bg-white rounded-2xl border border-yellow-100 p-6 shadow-xl">
            <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
              <Heart className="h-6 w-6 text-yellow-500" />
            </div>
            <h4 className="text-lg font-bold mb-2">Parent dashboard</h4>
            <p className="text-gray-600">Monitor favorites, share highlights, and download printable keepsakes.</p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default StoriesEmptyState;

